import { useMemo } from 'react';
import { assert } from '@varos/util-typescript';
import { ReportingShellLoaderBaseConfig } from '../../../base';
import { InsertOverviewLoader } from './InsertOverviewInterface';
import { InsertOverviewAggregate } from './InsertOverviewModel';
import { TemplateItemAggregate } from '../../template';

export function createInsertOverviewLoader(
    config: ReportingShellLoaderBaseConfig
): InsertOverviewLoader {
    const {
        repository,
        kernel: {
            infra: { formatter },
        },
    } = config;

    return {
        useLoad(context, props): InsertOverviewAggregate {
            // Use platform integration tool repository to fetch integration tools
            const integrationToolQuery = repository.platform.integrationTool.useFind(
                context,
                {},
                { suspense: true }
            );
            assert(integrationToolQuery.status === 'success', 'expected suspense');

            // Use component repository to fetch components
            const componentQuery = repository.research.component.useFind(
                context,
                {},
                { suspense: true }
            );
            assert(componentQuery.status === 'success', 'expected suspense');

            // Use platform integration definition repository to fetch integration definitions
            const integrationDefinitionQuery =
                repository.platform.integrationDefinition.useFind(
                    context,
                    {},
                    { suspense: true }
                );
            assert(integrationDefinitionQuery.status === 'success', 'expected suspense');

            const integrationTools = integrationToolQuery.data;
            const components = componentQuery.data;
            const integrationDefinitions = integrationDefinitionQuery.data;

            // Memoize the entire aggregate to ensure stable references
            const aggregate = useMemo(() => {
                // Create a map of definition ID to definition for quick lookup
                const definitionMap = new Map(
                    integrationDefinitions.map((def) => [def.id, def])
                );

                // Build template aggregates by combining each tool with its definition
                const templates = integrationTools.map((tool): TemplateItemAggregate => {
                    const definition = definitionMap.get(tool.integration_definition_id);
                    if (!definition) {
                        throw new Error(
                            `Definition not found for tool ${tool.id} with definition ID ${tool.integration_definition_id}`
                        );
                    }
                    return {
                        tool,
                        definition,
                    };
                });

                return {
                    templates,
                    components,
                };
            }, [integrationTools, integrationDefinitions, components]);

            return aggregate;
        },
    };
}
